import first from "lodash/first";
import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import type { authorRelatedTopicsAuthor$key } from "scmp-app/queries/__generated__/authorRelatedTopicsAuthor.graphql";

import { Container, Label, TopicLink } from "./styles";

type Props = {
  reference: authorRelatedTopicsAuthor$key;
};

export const AuthorRelatedTopics: FunctionComponent<Props> = ({ reference }) => {
  const author = useFragment(
    graphql`
      fragment authorRelatedTopicsAuthor on Author
      @argumentDefinitions(
        after: { type: "String" }
        applicationIds: { type: "[String]", defaultValue: [] }
        count: { type: "Int", defaultValue: 30 }
      ) {
        contents(
          after: $after
          first: $count
          filter: { applicationIds: $applicationIds }
          orderBy: { field: PUBLISHED_DATE, direction: DESC }
        ) {
          edges {
            node {
              topics {
                entityId
                name
                ...entityLink
              }
            }
          }
        }
      }
    `,
    reference,
  );

  const mainTopics = useMemo(() => {
    const mainTopics =
      author.contents?.edges.map(({ node }) => first(node.topics)).filter(notEmpty) ?? [];
    return new Map(mainTopics.map(topic => [topic.entityId, topic]))
      .values()
      .toArray()
      .slice(0, 10);
  }, [author.contents?.edges]);

  return (
    <Container>
      <Label>{mainTopics.length === 1 ? "Related Topic" : "Related Topics"}</Label>
      {mainTopics.length > 0
        ? mainTopics.map(topic => (
            <TopicLink key={topic.entityId} reference={topic}>
              {topic.name}
            </TopicLink>
          ))
        : `No Related Topics`}
    </Container>
  );
};

AuthorRelatedTopics.displayName = "AuthorRelatedTopics";
